<script setup lang="ts">

import { ref, onMounted, onUnmounted } from "vue";
import { useDisplay } from 'vuetify';

const model = defineModel<string|number|null>( { required: false });
const { mandatory = false } = defineProps<{ mandatory?: boolean }>();
const emit = defineEmits( [ 'on-select'] );
const display = useDisplay();
const content = ref();
let clickX = 0;

onMounted( () => { attach() } );
onUnmounted( () => { detach() } );

const attach = () => 
{

    const el = content.value as HTMLElement;
    if( el == null ) return;

    Array.from( el.children ).forEach( ( child ) =>
    {
        const elem = child as HTMLElement
        elem.style.userSelect = 'none' 
    } )

    el.addEventListener( 'mousedown', onMouseDown );
    el.addEventListener( 'mouseup', onMouseUp );
    el.addEventListener( 'mouseleave', onMouseUp );

};

const detach = () => 
{

    const el = content.value as HTMLElement;
    if( el == null ) return;

    el.removeEventListener( 'mousedown', onMouseDown );
    el.removeEventListener( 'mouseup', onMouseUp );
    el.removeEventListener( 'mouseleave', onMouseUp );

}

const onMouseDown = ( e: MouseEvent ) => 
{
    if( content.value == null ) return;
    clickX = e.x; 
    content.value.addEventListener( 'mousemove', onMouseMove );
}

const onMouseUp = ( e: MouseEvent ) =>
{
    if( content.value == null ) return;
    handleSelect( e );
    content.value.removeEventListener( 'mousemove', onMouseMove );
}

const onMouseMove = ( e: MouseEvent ) => 
{
    if( display.mobile.value ) return;
    const el = content.value as HTMLElement;
    el.scrollLeft = el.scrollLeft + e.movementX * -1
}

/**
 * Handles the selection of the model value and scrolling into view 
 * of the clicked item to ensure it is fully visible within the container
 * @param e 
 */
const handleSelect = ( e: MouseEvent ) =>
{

    const distance = Math.abs( clickX - e.x );
    if( distance > 10 ) return;

    const targetEl = e.target as any;
    const valueEl = targetEl?.closest('[value]');
    if( valueEl != null )
    {
        const value = valueEl.getAttribute( 'value' );
        model.value = ( value != model.value || mandatory ) ? value : null;
        emit( 'on-select', model.value );
    } 

    const itemEl = findItemElement( targetEl );
    if( itemEl == null ) return;

    const rect = itemEl.getBoundingClientRect();
    const contentEl = content.value as HTMLElement;
    const contentRect = contentEl.getBoundingClientRect();

    const offsetX = ( contentEl.children[ 0 ] as HTMLElement ).offsetLeft;
    const x = rect.left < contentRect.left ? itemEl.offsetLeft - offsetX
            : rect.right > contentRect.right ? itemEl.offsetLeft + rect.width + offsetX - contentRect.width
            : contentEl.scrollLeft;

    contentEl.scrollTo( { left: x, behavior: 'smooth' } );

}

/**
 * Look for the topmost parent element from the child element, that is an 
 * immediate child of the content element
 * @param childEl 
 */
function findItemElement( childEl: HTMLElement ): HTMLElement | null
{
    const parent = childEl.parentElement;
    if( parent == content.value ) return childEl;
    if( parent == null ) return null;
    return findItemElement( parent );
}

</script>

<template>
    
    <div class="w-full overflow-hidden transform-gpu">
        <div ref="content"
             class="horizontal-drag-scroll w-full flex overflow-scroll items-center">
            <slot>
            
            </slot>
        </div>
    </div>

</template>

<style scoped lang="scss">
    .horizontal-drag-scroll
    {
        overflow-x: auto;
        /* Enables the horizontal scrollbar */
        /* Next lines hides scrollbar in different browsers for styling purposes */
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
        &::-webkit-scrollbar /* Safari and Chrome */
        { 
            display: none;  
        }
    }
</style>